import { Capabilities, Padding, PlatformType } from "@spawn/common";
import { Align, DrawableRectangle, Layout, Orientation, Stretch, View, inject, view } from "@spawn/core";
import { FreegameService } from "@spawn/freegames";
import { GoldenBonusOverlayView } from "@spawn/golden-bonus";
import { NudgingService } from "@spawn/nudge";
import { RevealOverlayView } from "@spawn/reveal";
import { GameDesignView, GameDesignViewOptions, GridLayoutPositions } from "@spawn/slot-machine";
import Victor from "victor";
import { NrgBackgroundView, GameSymbolSpotlightView, NudgeUpsCounterView, ReelSetContainerView } from "@spawn/nrg-games-core";
import { MultiplerNotchView } from "./freegames/multiplierNotchView";
import { ReelSetFrameView } from "./reelSetFrameView";
import { NrgWinPartyView } from "./winPartyView";

@view
export class DesignView extends GameDesignView {
	@inject private readonly nudgingService!: NudgingService;
	@inject private readonly freegameService!: FreegameService;

	multiplierNotchView!: MultiplerNotchView;
	nudgeUpsCounterView!: NudgeUpsCounterView;

	constructor() {
		super({
			topGridHeightDesktop: 213,
			bottomGridHeightDesktop: 180,
			topGridHeightLandscape: 70,
			bottomGridHeightLandscape: 187,
			additionalReelSetContainerSize: new Victor(0, 0),
		});

		this.subscriptions.add(this.freegameService.isActive$.subscribe((isActive) => this.toggleMultiplierNotch(isActive)));
	}

	protected override init(): void {
		// Start with background.ds
		const backgroundView = this.createBackground();
		this.addChild(backgroundView);

		const gridLayoutView = this.createGridLayout();
		this.addChild(gridLayoutView);

		// Add reel set container to grid.
		this.createReelSetContainer();
		this.gridLayoutView.getCell(GridLayoutPositions.ReelSet).addChild(this.gameContainerView);

		// Add reel set frame to reel set container.
		const reelSetFrameView = this.createReelSetFrame();
		this.gameContainerView.addChild(reelSetFrameView);

		// Add reel set to reel set container.
		const reelSetView = this.createReelSet();
		this.gameContainerView.addChild(reelSetView);

		this.setupGoldenBonus();

		// Add symbol spot light view to reel set container.
		const symbolSpotlightView = this.createSymbolSpotlight();
		this.gameContainerView.addChild(symbolSpotlightView);

		this.setupReveal();
		this.setupNudging();

		// Add nudge ups counter to reel set container.
		this.createNudgeUpsCounter();
		this.gameContainerView.addChild(this.nudgeUpsCounterView);

		// Add multiplier notch to reel set container.
		this.createMultiplierNotch();
		this.gameContainerView.addChild(this.multiplierNotchView);

		// Add win party view to root and container party to grid.
		// On mobile the win party is added to the game container,
		// to make sure the coin shower is in the right place.
		const winPartyView = this.createWinParty();
		if (Capabilities.platformType === PlatformType.Desktop) {
			this.addChild(winPartyView);
		} else {
			this.gameContainerView.addChild(winPartyView);
		}
		this.gameContainerView.addChild(winPartyView.winContainerView);

		// Debugging for grid layout.
		if (this.overridesService.overrides.debugGridLayout) {
			this.debugGridLayoutView();
		}

		// Adjust grid layout to the new added views.
		this.updateGridLayout();

		// Add subscriptions.
		this.subscriptions.add(this.scaleProvider.aspectRatio$.subscribe(() => this.updateGridLayout()));
	}

	protected override createBackground(): View {
		this.backgroundView = new NrgBackgroundView();
		return this.backgroundView;
	}

	protected override updateGridLayout(): void {
		super.updateGridLayout();
		this.updateLayout();
	}

	protected updateLayout(): void {
		if (Capabilities.platformType !== PlatformType.Desktop) {
			if (this.screenOrientationService.orientation === Orientation.Portrait) {
				this.gameContainerView.layout = new Layout(Stretch.ContainHorizontal(Align.CenterHorizontal), Align.Bottom);
				this.gameContainerView.layout.offset = new Victor(0, -39);
				if (Capabilities.platformType === PlatformType.Tablet) {
					this.gameContainerView.layout.padding = new Padding({ left: 60, right: 60 });
					this.gameContainerView.layout.offset = new Victor(0, -37);
				}
			} else if (Capabilities.platformType === PlatformType.Tablet) {
				this.gameContainerView.layout = new Layout(Align.CenterHorizontal, Align.CenterVertical);
				this.gameContainerView.layout.offset = new Victor(0, -18);
			} else {
				this.gameContainerView.layout.padding = new Padding({ bottom: 45 });
			}
		}
	}

	protected override createReelSetFrame(): View {
		this.reelSetFrameView = new ReelSetFrameView();
		this.reelSetFrameView.name = "ReelSetFrameView";
		return this.reelSetFrameView;
	}

	protected override createReelSet(): View {
		this.reelSetView = new ReelSetContainerView();
		this.reelSetView.name = "ReelSetView";
		return this.reelSetView;
	}

	protected override createWinParty(): NrgWinPartyView {
		const view = new NrgWinPartyView();
		view.name = "WinPartyView";
		this.winPartyView = view;
		return view;
	}

	protected override createSymbolSpotlight(reelSetName = "BaseGame"): View {
		this.symbolSpotlightView = new GameSymbolSpotlightView(reelSetName);
		this.symbolSpotlightView.name = "SymbolSpotlightView";

		this.symbolSpotlightView.setMask(this.createMask(reelSetName, this.options));

		return this.symbolSpotlightView;
	}

	private createMultiplierNotch(): MultiplerNotchView {
		this.multiplierNotchView = new MultiplerNotchView();
		this.multiplierNotchView.name = "MultiplierNotchView";
		this.multiplierNotchView.layout = new Layout(Align.CenterHorizontal, Align.Top);
		this.multiplierNotchView.layout.offset = new Victor(0, -33);
		return this.multiplierNotchView;
	}

	private toggleMultiplierNotch(isActive: boolean): void {
		this.multiplierNotchView.visible = isActive;
	}

	private createNudgeUpsCounter(): NudgeUpsCounterView {
		this.nudgeUpsCounterView = new NudgeUpsCounterView();
		this.nudgeUpsCounterView.name = "NudgeUpsCounterView";
		this.nudgeUpsCounterView.layout = new Layout(Align.CenterHorizontal, Align.Top);
		return this.nudgeUpsCounterView;
	}

	private setupReveal(): void {
		const view = new RevealOverlayView();
		const config = this.gameConfigService.getReelSetViewConfig("BaseGame");
		view.setup(config.visibleSymbols);

		const mask = this.createMask(view.reelSetName, this.options);
		mask.position = new Victor(0, 0);
		view.setMask(mask);

		this.gameContainerView.addChild(view);
	}

	private setupNudging(): void {
		const view = this.nudgingService.view;
		const config = this.gameConfigService.getReelSetViewConfig("BaseGame");
		const mask = this.createMask(view.reelSetName, this.options);

		// Mask needs to be offset by one position, because nudging overlay has one extra symbol
		// for the animation.
		mask.position = new Victor(0, config.symbolSize.y);
		// We need the 2 extra Symbols for when a reel is nudged up/down.
		view.setup(config.visibleSymbols + 2);
		view.setMask(mask);

		this.gameContainerView.addChild(view);
	}

	private setupGoldenBonus(): void {
		const goldenBonusView = new GoldenBonusOverlayView();
		const mask = this.createMask("BaseGame", this.options);

		goldenBonusView.setMask(mask);
		goldenBonusView.name = "GoldenBonusOverlayView";

		this.gameContainerView.addChild(goldenBonusView);
	}

	private createMask(reelSetName: string, options: GameDesignViewOptions): DrawableRectangle {
		const config = this.gameConfigService.getReelSetViewConfig(reelSetName);

		let w = config.reelCount * config.symbolSize.x + (config.reelCount - 1) * config.spaceBetweenReels;
		let h = config.visibleSymbols * config.symbolSize.y;

		if (options?.additionalReelSetContainerSize) {
			w += options.additionalReelSetContainerSize.x;
			h += options.additionalReelSetContainerSize.y;
		}

		return new DrawableRectangle({ size: new Victor(w, h) });
	}
}
